import React, { useState, useEffect, useRef } from 'react';
import { Container, Form, Button, Image, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { apiUrl } from '../apiUrl';

const INDIAN_STATES = [
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  'Andaman and Nicobar Islands',
  'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi',
  'Jammu and Kashmir',
  'Ladakh',
  'Lakshadweep',
  'Puducherry',
];

const descriptiveTextError = (label: string, value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return `${label} is required.`;
  if (/^\d+$/.test(trimmed)) return `${label} must include descriptive text, not only numbers.`;
  return '';
};

const BecomeMember: React.FC = () => {
  const [formData, setFormData] = useState({
    memberId: '',
    fullName: '',
    mobile: '',
    email: '',
    aadhar: '',
    referenceBy: '',
    gender: '',
    dateOfBirth: '',
    fathersName: '',
    state: '',
    city: '',
    pinCode: '',
    address: '',
    nationality: 'India',
    qualification: '',
    areaOfOperation: '',
    experience: '',
    briefAboutYourself: '',
    termsAccepted: false,
    photo: null as File | null,
    resume: null as File | null,
  });
  const [result, setResult] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);
  const [previewPhoto, setPreviewPhoto] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false); // Modal visibility state
  const [showStateOptions, setShowStateOptions] = useState(false);
  const [stateSearchValue, setStateSearchValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const pending = useRef(false);
  const mounted = useRef(true);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ qualification?: string; areaOfOperation?: string }>({});

  useEffect(() => {
    const generateMemberId = () => {
      const randomId = 'MEM' + Math.floor(1000 + Math.random() * 9000);
      setFormData((prev) => ({ ...prev, memberId: randomId }));
    };
    generateMemberId();
  }, []);

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const showResult = (kind: 'success' | 'error', message: string) => { setResult({ kind, message }); setShowModal(true); };
  const closeModal = () => setShowModal(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'qualification' || name === 'areaOfOperation') {
      const label = name === 'qualification' ? 'Qualification' : 'Area of Operation';
      setFieldErrors((previous) => ({ ...previous, [name]: descriptiveTextError(label, value) || undefined }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, field: 'photo' | 'resume') => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setFormData((prev) => ({ ...prev, [field]: file }));
      if (field === 'photo' && file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewPhoto(reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, termsAccepted: e.target.checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pending.current || uncertain) return;

    const qualificationError = descriptiveTextError('Qualification', formData.qualification);
    const areaOfOperationError = descriptiveTextError('Area of Operation', formData.areaOfOperation);
    if (qualificationError || areaOfOperationError) {
      setFieldErrors({ qualification: qualificationError || undefined, areaOfOperation: areaOfOperationError || undefined });
      return;
    }

    if (!formData.termsAccepted) {
      showResult('error', 'Please accept the Terms and Conditions.');
      return;
    }
    const hasValidState = INDIAN_STATES.some(
      (option) => option.toLowerCase() === formData.state.trim().toLowerCase()
    );
    if (!hasValidState) {
      showResult('error', 'Please select a valid state from the dropdown list.');
      return;
    }

    const formDataToSend = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (value instanceof File) {
        formDataToSend.append(key, value);
      } else if (value !== null) {
        formDataToSend.append(key, value.toString());
      }
    });

    pending.current = true;
    try {
      setIsSubmitting(true);
      const res = await fetch(apiUrl('/api/members'), {
        method: 'POST',
        body: formDataToSend,
      });

      const data = await res.json().catch(() => null);
      if (!mounted.current) return;
      if (res.ok) {
        if (!data || typeof data.emailQueued !== 'boolean') throw new Error('Uncertain response');
        setValidationAttempted(false);
        setFieldErrors({});
        showResult('success',
          data.emailQueued
            ? 'Thank you for registering as a member!'
            : 'Application submitted. Confirmation email could not be queued; please contact the Foundation if needed.'
        );

        const randomId = 'MEM' + Math.floor(1000 + Math.random() * 9000);
        setFormData({
          memberId: randomId,
          fullName: '',
          mobile: '',
          email: '',
          aadhar: '',
          referenceBy: '',
          gender: '',
          dateOfBirth: '',
          fathersName: '',
          state: '',
          city: '',
          pinCode: '',
          address: '',
          nationality: 'India',
          qualification: '',
          areaOfOperation: '',
          experience: '',
          briefAboutYourself: '',
          termsAccepted: false,
          photo: null,
          resume: null,
        });
        setStateSearchValue('');
        setShowStateOptions(false);
        setPreviewPhoto(null);
      } else {
        if (res.status >= 500) { setUncertain(true); showResult('error', 'We could not confirm whether your application was saved. Please contact the Foundation before retrying to avoid a duplicate.'); }
        else showResult('error', data?.message || 'Error submitting form.');
      }
    } catch (err) {
      if (mounted.current) { setUncertain(true); showResult('error', 'We could not confirm whether your application was saved. Please contact the Foundation before retrying to avoid a duplicate.'); }
    } finally {
      pending.current = false;
      if (mounted.current) setIsSubmitting(false);
    }
  };

  return (
    <section className="sf-involvement-page">
      <div className="sf-involvement-intro tw-py-[clamp(3rem,6vw,5rem)]">
        <Container className="sf-involvement-container">
          <p className="sf-involvement-eyebrow">GET INVOLVED</p>
          <h1>Become a Member</h1>
          <p className="sf-involvement-lead">Join us in supporting free education and learning for children studying up to Class 8.</p>
        </Container>
      </div>

      <Container className="sf-involvement-container tw-py-[clamp(2.8rem,5vw,4.4rem)]">
        <p>Fields marked * are required. Mobile numbers must have 10 digits and PIN codes 6 digits. Optional Aadhaar numbers must have 12 digits.</p>
        <Form onSubmit={handleSubmit} onInvalidCapture={() => setValidationAttempted(true)} className={validationAttempted ? 'sf-validation-attempted' : undefined}>
        {validationAttempted && <p className="sf-validation-summary" role="alert">Please correct the highlighted fields. Your browser will explain the first error.</p>}
        <div className="tw-grid tw-gap-6 md:tw-grid-cols-2 tw-items-start">
          {/* Left Section (Col-6) */}
          <div className="sf-involvement-card">
              <h2>Personal Details</h2>
                <Form.Group className="mb-3" controlId="formMemberId">
                  <Form.Label className="d-none">Member ID</Form.Label>
                  <Form.Control
                    type="text"
                    name="memberId"
                    value={formData.memberId}
                    readOnly
                    className="form-control-custom d-none"
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formFullName">
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formMobile">
                  <Form.Label>Mobile</Form.Label>
                  <Form.Control
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="Enter your mobile number"
                    pattern="[0-9]{10}"
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formEmail">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formAadhar">
                  <Form.Label>Aadhar (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    name="aadhar"
                    value={formData.aadhar}
                    onChange={handleChange}
                    placeholder="Enter your Aadhar number"
                    pattern="[0-9]{12}"
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formReferenceBy">
                  <Form.Label>Reference By (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    name="referenceBy"
                    value={formData.referenceBy}
                    onChange={handleChange}
                    placeholder="Who referred you?"
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formGender">
                  <Form.Label>Gender</Form.Label>
                  <Form.Control
                    as="select"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </Form.Control>
                </Form.Group>
                <Form.Group className="mb-3" controlId="formDateOfBirth">
                  <Form.Label>Date of Birth</Form.Label>
                  <Form.Control
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formFathersName">
                  <Form.Label>Father’s Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="fathersName"
                    value={formData.fathersName}
                    onChange={handleChange}
                    placeholder="Enter your father’s name"
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formState">
                  <Form.Label>State</Form.Label>
                  <div style={{ position: 'relative' }}>
                    <Form.Control
                      type="text"
                      value={stateSearchValue}
                      onFocus={() => setShowStateOptions(true)}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setStateSearchValue(e.target.value);
                        setShowStateOptions(true);
                      }}
                      onBlur={() => {
                        const matched = INDIAN_STATES.find(
                          (option) => option.toLowerCase() === stateSearchValue.trim().toLowerCase()
                        );
                        if (matched) {
                          setStateSearchValue(matched);
                          setFormData((prev) => ({ ...prev, state: matched }));
                        } else {
                          setStateSearchValue(formData.state || '');
                        }
                        setTimeout(() => setShowStateOptions(false), 120);
                      }}
                      placeholder="Search and select your state"
                      required
                    />
                    {showStateOptions && (
                      <div
                        className="list-group"
                        style={{
                          position: 'absolute',
                          zIndex: 20,
                          width: '100%',
                          maxHeight: '180px',
                          overflowY: 'auto',
                          border: '1px solid #dfe5ef',
                          borderRadius: '8px',
                          marginTop: '6px',
                          background: '#fff',
                        }}
                      >
                        {INDIAN_STATES.filter((option) =>
                          option.toLowerCase().includes(stateSearchValue.trim().toLowerCase())
                        )
                          .slice(0, 50)
                          .map((option) => (
                            <button
                              key={option}
                              type="button"
                              className="list-group-item list-group-item-action"
                              onMouseDown={(evt) => evt.preventDefault()}
                              onClick={() => {
                                setStateSearchValue(option);
                                setFormData((prev) => ({ ...prev, state: option }));
                                setShowStateOptions(false);
                              }}
                            >
                              {option}
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                </Form.Group>
                <Form.Group className="mb-3" controlId="formCity">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Enter your city"
                    required
                  />
                </Form.Group>
          </div>

          {/* Right Section (Col-6) */}
          <div className="sf-involvement-card">
              <h2>Additional Details</h2>
              {/* Photo Preview at the Top Right */}
              {previewPhoto && (
                <div className="photo-preview mb-3">
                  <Image src={previewPhoto} alt="Preview" rounded fluid />
                </div>
              )}
                <Form.Group className="mb-3" controlId="formPinCode">
                  <Form.Label>Pin Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="pinCode"
                    value={formData.pinCode}
                    onChange={handleChange}
                    placeholder="Enter your pin code"
                    pattern="[0-9]{6}"
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formAddress">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Enter your address"
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formNationality">
                  <Form.Label>Nationality</Form.Label>
                  <Form.Control
                    as="select"
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleChange}
                    required
                  >
                    <option value="India">India</option>
                    <option value="Other">Other</option>
                  </Form.Control>
                </Form.Group>
                <Form.Group className="mb-3" controlId="formQualification">
                  <Form.Label>Qualification</Form.Label>
                  <Form.Control
                    type="text"
                    name="qualification"
                    value={formData.qualification}
                    onChange={handleChange}
                    placeholder="Enter your qualification"
                    required
                    isInvalid={Boolean(fieldErrors.qualification)}
                    aria-describedby={fieldErrors.qualification ? 'qualification-error' : undefined}
                    onInvalid={() => setFieldErrors((previous) => ({ ...previous, qualification: descriptiveTextError('Qualification', formData.qualification) }))}
                  />
                  {fieldErrors.qualification && <Form.Control.Feedback id="qualification-error" type="invalid">{fieldErrors.qualification}</Form.Control.Feedback>}
                </Form.Group>
                <Form.Group className="mb-3" controlId="formAreaOfOperation">
                  <Form.Label>Area of Operation</Form.Label>
                  <Form.Control
                    type="text"
                    name="areaOfOperation"
                    value={formData.areaOfOperation}
                    onChange={handleChange}
                    placeholder="Enter your area of operation"
                    required
                    isInvalid={Boolean(fieldErrors.areaOfOperation)}
                    aria-describedby={fieldErrors.areaOfOperation ? 'area-of-operation-error' : undefined}
                    onInvalid={() => setFieldErrors((previous) => ({ ...previous, areaOfOperation: descriptiveTextError('Area of Operation', formData.areaOfOperation) }))}
                  />
                  {fieldErrors.areaOfOperation && <Form.Control.Feedback id="area-of-operation-error" type="invalid">{fieldErrors.areaOfOperation}</Form.Control.Feedback>}
                </Form.Group>
                <Form.Group className="mb-3" controlId="formExperience">
                  <Form.Label>Experience (if any)</Form.Label>
                  <Form.Control
                    type="text"
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    placeholder="Enter your experience"
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formBriefAboutYourself">
                  <Form.Label>Brief About Yourself</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="briefAboutYourself"
                    value={formData.briefAboutYourself}
                    onChange={handleChange}
                    placeholder="Tell us about yourself"
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formPhoto">
                  <Form.Label>Photo (Optional)</Form.Label>
                  <Form.Control
                    type="file"
                    name="photo"
                    accept="image/*"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFileChange(e, 'photo')}
                  />
                  <Form.Text>Optional image file.</Form.Text>
                </Form.Group>
                <Form.Group className="mb-3" controlId="formResume">
                  <Form.Label>Resume (Optional)</Form.Label>
                  <Form.Control
                    type="file"
                    name="resume"
                    accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFileChange(e, 'resume')}
                  />
                  <Form.Text>Optional résumé: PDF, DOC or DOCX.</Form.Text>
                </Form.Group>
                <Form.Group className="mb-3" controlId="formTerms">
                  <Form.Check
                    type="checkbox"
                    label="I accept the Terms and Conditions *"
                    name="termsAccepted"
                    checked={formData.termsAccepted}
                    onChange={handleCheckboxChange}
                    required
                  />
                </Form.Group>
                <div className="text-center">
                  {uncertain && <p role="status">The submission result is uncertain. Please contact the Foundation before trying again.</p>}
                  <Button
                    variant="primary"
                    type="submit"
                    className="sf-involvement-submit"
                    disabled={isSubmitting || uncertain}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Application'}
                  </Button>
                </div>
          </div>
        </div>
        </Form>

        {/* Success/Error Modal */}
        <Modal show={showModal} onHide={closeModal} centered>
          <Modal.Body className="text-center">
            {result?.kind === 'success' ? (
              <div className="d-flex flex-column align-items-center">
                <FontAwesomeIcon icon={faCheckCircle} size="3x" className="text-success mb-3" />
                <h4 className="text-success mb-2">Success!</h4>
                <p>{result.message}</p>
              </div>
            ) : (
              <div className="d-flex flex-column align-items-center">
                <FontAwesomeIcon icon={faTimesCircle} size="3x" className="text-danger mb-3" />
                <h4 className="text-danger mb-2">Error!</h4>
                <p>{result?.message}</p>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer><Button variant="secondary" onClick={closeModal}>Close</Button></Modal.Footer>
        </Modal>
      </Container>
    </section>
  );
};

export default BecomeMember;
